import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ShoppingBag, Save } from "lucide-react";
import { adminApi } from "@/services/adminApi";
import { queryKeys } from "@/lib/queryKeys";
import type { ProductResponse, ProductVariantResponse } from "@printtat/shared";
import {
  AlertDialog,
  PageHeader,
  PageContent,
  Button,
  IconButton,
  Label,
  LoadingState,
  Tabs,
  Table,
  TextField,
} from '@printtat/ui';
import type { Column } from '@printtat/ui';
import { PrintAreasEditor } from "@/components/products/PrintAreasEditor";
import { PricingEditor } from "@/components/products/PricingEditor";

type TabKey = "variants" | "printAreas" | "pricing";

const TABS: { key: TabKey; label: string }[] = [
  { key: "variants", label: "Variants" },
  { key: "printAreas", label: "Print Areas" },
  { key: "pricing", label: "Pricing" },
];

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

interface VariantEdits {
  [variantId: string]: {
    additionalPrice?: string;
    stockCount?: string;
    isActive?: boolean;
    isDefault?: boolean;
  };
}

interface PrintAreas {
  surfaces: string[];
  dimensions: Record<string, { width: number; height: number }>;
  canvasOffset: Record<string, { x: number; y: number }>;
  canvasWidthRatio: Record<string, number>;
  safeAreas: Record<string, { margin: number }>;
  mockups: Record<string, string>;
  surfaceLabels: Record<string, string>;
}

interface PricingConfig {
  currency: string;
  basePrintPrice: number;
  surfacePrices: Record<string, number>;
  coverageBrackets: Record<
    string,
    { max: number; label: string; price: number }[]
  >;
  surfaceOptions?: Record<
    string,
    {
      key: string;
      label: string;
      type: string;
      choices: { value: string; label: string; price: number }[];
      default: string;
    }[]
  >;
}

export const AdminProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabKey>("variants");
  const [showConfirm, setShowConfirm] = useState(false);

  // Product details local edits
  const [detailEdits, setDetailEdits] = useState<{
    name?: string;
    description?: string;
    basePrice?: string;
  }>({});
  const [variantEdits, setVariantEdits] = useState<VariantEdits>({});

  // Local state for print areas and pricing editors
  const [printAreasLocal, setPrintAreasLocal] = useState<PrintAreas | null>(
    null,
  );
  const [pricingLocal, setPricingLocal] = useState<PricingConfig | null>(null);
  const [printAreasDirty, setPrintAreasDirty] = useState(false);
  const [pricingDirty, setPricingDirty] = useState(false);
  const [recalcResult, setRecalcResult] = useState<number | null>(null);

  const { data: product, isLoading } = useQuery<ProductResponse>({
    queryKey: queryKeys.products.detail(id!),
    queryFn: () => adminApi.products.detail(id!),
    enabled: !!id,
  });

  // Sync local state when product data loads or changes
  useEffect(() => {
    if (product) {
      if (!printAreasDirty) {
        setPrintAreasLocal(product.printAreas as PrintAreas);
      }
      if (!pricingDirty) {
        setPricingLocal(product.pricingConfig as PricingConfig | null);
      }
    }
  }, [product, printAreasDirty, pricingDirty]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
    queryClient.invalidateQueries({ queryKey: ["products"] });
  };

  // --- Dirty flags ---

  const detailsDirty = Object.keys(detailEdits).length > 0;
  const variantsDirty = Object.keys(variantEdits).length > 0;
  const anyDirty =
    detailsDirty || variantsDirty || printAreasDirty || pricingDirty;

  // --- Mutations ---

  const updateProduct = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      adminApi.products.updateProduct(id!, data),
    onSuccess: () => invalidateAll(),
  });

  // Will be wired to a UI button; referenced via void to satisfy noUnusedLocals.
  const recalculatePrices = useMutation({
    mutationFn: () => adminApi.products.recalculatePrices(id!),
    onSuccess: (result) => {
      setRecalcResult(result.recalculated);
      queryClient.invalidateQueries({ queryKey: ["designs"] });
    },
  });
  void recalculatePrices;

  const saveAll = useMutation({
    mutationFn: async () => {
      // 1. Product details
      if (detailsDirty) {
        const data: Record<string, unknown> = {};
        if (detailEdits.name !== undefined) data.name = detailEdits.name;
        if (detailEdits.description !== undefined)
          data.description = detailEdits.description;
        if (detailEdits.basePrice !== undefined) {
          const parsed = parseFloat(detailEdits.basePrice);
          if (!isNaN(parsed) && parsed >= 0) data.basePrice = parsed;
        }
        await adminApi.products.updateProduct(id!, data);
      }

      // 2. Variants — isDefault must be applied before other fields on the
      // promoted variant so the backend's partial unique index sees the prior
      // default demoted in the same request scope.
      if (variantsDirty) {
        const entries = Object.entries(variantEdits);
        entries.sort(([, a], [, b]) =>
          a.isDefault === true ? -1 : b.isDefault === true ? 1 : 0,
        );
        for (const [variantId, edits] of entries) {
          const data: {
            isActive?: boolean;
            additionalPrice?: number;
            stockCount?: number;
            isDefault?: boolean;
          } = {};
          if (edits.isActive !== undefined) data.isActive = edits.isActive;
          if (edits.isDefault !== undefined) data.isDefault = edits.isDefault;
          if (edits.additionalPrice !== undefined) {
            const parsed = parseFloat(edits.additionalPrice);
            if (!isNaN(parsed) && parsed >= 0) data.additionalPrice = parsed;
          }
          if (edits.stockCount !== undefined) {
            const parsed = parseInt(edits.stockCount, 10);
            if (!isNaN(parsed) && parsed >= 0) data.stockCount = parsed;
          }
          if (Object.keys(data).length > 0) {
            await adminApi.products.updateVariant(id!, variantId, data);
          }
        }
      }

      // 3. Print areas
      if (printAreasDirty) {
        await adminApi.products.updateProduct(id!, {
          printAreas: printAreasLocal,
        });
      }

      // 4. Pricing
      if (pricingDirty) {
        await adminApi.products.updateProduct(id!, {
          pricingConfig: pricingLocal,
        });
      }
    },
    onSuccess: () => {
      invalidateAll();
      setDetailEdits({});
      setVariantEdits({});
      setPrintAreasDirty(false);
      setPricingDirty(false);
      queryClient.invalidateQueries({ queryKey: ["designs"] });
    },
  });

  // --- Handlers ---

  const handleDiscard = () => {
    setDetailEdits({});
    setVariantEdits({});
    if (product) {
      setPrintAreasLocal(product.printAreas as PrintAreas);
      setPricingLocal(product.pricingConfig as PricingConfig | null);
    }
    setPrintAreasDirty(false);
    setPricingDirty(false);
  };

  const handleConfirmSave = () => {
    setShowConfirm(false);
    saveAll.mutate();
  };

  if (isLoading || !product) {
    return (
      <PageContent>
        <LoadingState variant="spinner" message="Loading product..." />
      </PageContent>
    );
  }

  // --- Change lines for confirmation modal ---

  const getAllChangeLines = (): { section: string; lines: string[] }[] => {
    const sections: { section: string; lines: string[] }[] = [];

    if (detailsDirty) {
      const lines: string[] = [];
      if (detailEdits.name !== undefined)
        lines.push(`Name: "${product.name}" → "${detailEdits.name}"`);
      if (detailEdits.description !== undefined)
        lines.push("Description updated");
      if (detailEdits.basePrice !== undefined)
        lines.push(
          `Base price: ${product.basePrice} EGP → ${detailEdits.basePrice} EGP`,
        );
      sections.push({ section: "Product Details", lines });
    }

    if (variantsDirty) {
      const lines: string[] = [];
      for (const [vid, edits] of Object.entries(variantEdits)) {
        const v = product.variants.find((x) => x.id === vid);
        if (!v) continue;
        const parts: string[] = [];
        if (edits.additionalPrice !== undefined)
          parts.push(
            `additional price: ${v.additionalPrice} → ${edits.additionalPrice}`,
          );
        if (edits.stockCount !== undefined)
          parts.push(`stock: ${v.stockCount} → ${edits.stockCount}`);
        if (edits.isActive !== undefined)
          parts.push(edits.isActive ? "activate" : "deactivate");
        if (edits.isDefault !== undefined)
          parts.push(edits.isDefault ? "set as default" : "unset default");
        if (parts.length > 0)
          lines.push(`${v.color} ${v.size} — ${parts.join(", ")}`);
      }
      sections.push({ section: "Variants", lines });
    }

    if (printAreasDirty && printAreasLocal) {
      const lines: string[] = [];
      const orig = product.printAreas as PrintAreas;

      // Added / removed surfaces
      const added = printAreasLocal.surfaces.filter(
        (s) => !orig.surfaces.includes(s),
      );
      const removed = orig.surfaces.filter(
        (s) => !printAreasLocal.surfaces.includes(s),
      );
      for (const s of added) lines.push(`Add surface "${s}"`);
      for (const s of removed) lines.push(`Remove surface "${s}"`);

      // Per-surface changes
      for (const s of printAreasLocal.surfaces) {
        if (!orig.surfaces.includes(s)) continue; // already listed as added
        const parts: string[] = [];
        const oLabel = orig.surfaceLabels?.[s] ?? s;
        const nLabel = printAreasLocal.surfaceLabels?.[s] ?? s;
        if (oLabel !== nLabel) parts.push(`label: "${oLabel}" → "${nLabel}"`);

        const oD = orig.dimensions?.[s];
        const nD = printAreasLocal.dimensions?.[s];
        if (oD && nD && (oD.width !== nD.width || oD.height !== nD.height))
          parts.push(`dimensions: ${oD.width}×${oD.height} → ${nD.width}×${nD.height}`);

        const oO = orig.canvasOffset?.[s];
        const nO = printAreasLocal.canvasOffset?.[s];
        if (oO && nO && (oO.x !== nO.x || oO.y !== nO.y))
          parts.push(`offset: (${oO.x}, ${oO.y}) → (${nO.x}, ${nO.y})`);

        const oR = orig.canvasWidthRatio?.[s];
        const nR = printAreasLocal.canvasWidthRatio?.[s];
        if (oR !== undefined && nR !== undefined && oR !== nR)
          parts.push(`width ratio: ${oR} → ${nR}`);

        const oSA = orig.safeAreas?.[s]?.margin;
        const nSA = printAreasLocal.safeAreas?.[s]?.margin;
        if (oSA !== undefined && nSA !== undefined && oSA !== nSA)
          parts.push(`safe area: ${oSA} → ${nSA}`);

        const oM = orig.mockups?.[s] ?? "";
        const nM = printAreasLocal.mockups?.[s] ?? "";
        if (oM !== nM) parts.push("mockup path updated");

        if (parts.length > 0)
          lines.push(`${nLabel} (${s}) — ${parts.join(", ")}`);
      }

      if (lines.length === 0) lines.push("Configuration will be updated");
      sections.push({ section: "Print Areas", lines });
    }

    if (pricingDirty && pricingLocal) {
      const lines: string[] = [];
      const orig = product.pricingConfig as PricingConfig | null;

      if (!orig) {
        lines.push("Pricing configuration added");
      } else {
        if (orig.currency !== pricingLocal.currency)
          lines.push(`Currency: "${orig.currency}" → "${pricingLocal.currency}"`);

        if (orig.basePrintPrice !== pricingLocal.basePrintPrice)
          lines.push(
            `Base print price: ${orig.basePrintPrice} → ${pricingLocal.basePrintPrice}`,
          );

        // Surface prices
        const allSurfaces = new Set([
          ...Object.keys(orig.surfacePrices ?? {}),
          ...Object.keys(pricingLocal.surfacePrices ?? {}),
        ]);
        for (const s of allSurfaces) {
          const oP = orig.surfacePrices?.[s];
          const nP = pricingLocal.surfacePrices?.[s];
          if (oP === undefined && nP !== undefined)
            lines.push(`${s} surface price added: ${nP}`);
          else if (oP !== undefined && nP === undefined)
            lines.push(`${s} surface price removed`);
          else if (oP !== nP)
            lines.push(`${s} surface price: ${oP} → ${nP}`);
        }

        // Coverage brackets
        const allBracketSurfaces = new Set([
          ...Object.keys(orig.coverageBrackets ?? {}),
          ...Object.keys(pricingLocal.coverageBrackets ?? {}),
        ]);
        for (const s of allBracketSurfaces) {
          const oB = JSON.stringify(orig.coverageBrackets?.[s] ?? []);
          const nB = JSON.stringify(pricingLocal.coverageBrackets?.[s] ?? []);
          if (oB !== nB)
            lines.push(`${s} coverage brackets updated`);
        }

        // Surface options
        const allOptionSurfaces = new Set([
          ...Object.keys(orig.surfaceOptions ?? {}),
          ...Object.keys(pricingLocal.surfaceOptions ?? {}),
        ]);
        for (const s of allOptionSurfaces) {
          const oO = JSON.stringify(orig.surfaceOptions?.[s] ?? []);
          const nO = JSON.stringify(pricingLocal.surfaceOptions?.[s] ?? []);
          if (oO !== nO)
            lines.push(`${s} surface options updated`);
        }
      }

      if (lines.length === 0) lines.push("Configuration will be updated");
      sections.push({ section: "Pricing", lines });
    }

    return sections;
  };

  // --- Variant handlers ---

  const handleVariantEdit = (
    variantId: string,
    field: "additionalPrice" | "stockCount" | "isActive" | "isDefault",
    value: string | boolean,
  ) => {
    setVariantEdits((prev) => ({
      ...prev,
      [variantId]: { ...prev[variantId], [field]: value },
    }));
  };

  const handleVariantToggle = (variant: ProductVariantResponse) => {
    const currentValue =
      variantEdits[variant.id]?.isActive ?? variant.isActive;
    handleVariantEdit(variant.id, "isActive", !currentValue);
  };

  // Only one variant per product can be the default. Clicking an unflagged
  // variant promotes it and demotes all siblings in the same dirty batch so
  // the backend's partial unique index is satisfied when the save runs.
  const handleVariantSetDefault = (variant: ProductVariantResponse) => {
    if (!product) return;
    setVariantEdits((prev) => {
      const next: VariantEdits = { ...prev };
      for (const v of product.variants) {
        if (v.id === variant.id) {
          next[v.id] = { ...next[v.id], isDefault: true };
        } else {
          const alreadyDefault = v.isDefault;
          const pendingDefault = prev[v.id]?.isDefault;
          if (alreadyDefault || pendingDefault) {
            next[v.id] = { ...next[v.id], isDefault: false };
          }
        }
      }
      return next;
    });
  };

  // --- Variant columns ---

  const variantColumns: Column<ProductVariantResponse>[] = [
    {
      key: "color",
      header: "Color",
      render: (v) => (
        <div className="flex items-center gap-2">
          <span
            className="w-5 h-5 rounded-full border border-gray-200"
            style={{ backgroundColor: v.colorHex }}
          />
          <span className="text-sm text-gray-900">{v.color}</span>
        </div>
      ),
    },
    {
      key: "size",
      header: "Size",
      render: (v) => <span className="text-sm text-gray-700">{v.size}</span>,
    },
    {
      key: "sku",
      header: "SKU",
      render: (v) => (
        <span className="text-xs font-mono text-gray-500">{v.sku}</span>
      ),
    },
    {
      key: "additionalPrice",
      header: "Additional Price",
      render: (v) => {
        const edit = variantEdits[v.id]?.additionalPrice;
        return (
          <TextField
            type="number"
            min="0"
            step="0.01"
            className="w-24"
            value={edit ?? String(v.additionalPrice)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleVariantEdit(v.id, "additionalPrice", e.target.value)
            }
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        );
      },
    },
    {
      key: "stockCount",
      header: "Stock",
      render: (v) => {
        const edit = variantEdits[v.id]?.stockCount;
        return (
          <TextField
            type="number"
            min="0"
            step="1"
            className="w-20"
            value={edit ?? String(v.stockCount)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleVariantEdit(v.id, "stockCount", e.target.value)
            }
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        );
      },
    },
    {
      key: "status",
      header: "Active",
      render: (v) => {
        const isActive = variantEdits[v.id]?.isActive ?? v.isActive;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleVariantToggle(v);
            }}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              isActive ? "bg-green-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                isActive ? "translate-x-4.5" : "translate-x-0.5"
              }`}
            />
          </button>
        );
      },
    },
    {
      key: "default",
      header: "Default",
      render: (v) => {
        const isDefault = variantEdits[v.id]?.isDefault ?? v.isDefault;
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!isDefault) handleVariantSetDefault(v);
            }}
            aria-label={
              isDefault
                ? `${v.color} ${v.size} is the default variant`
                : `Make ${v.color} ${v.size} the default variant`
            }
            className={`h-4 w-4 rounded-full border-2 transition-colors ${
              isDefault
                ? "border-green-500 bg-green-500"
                : "border-gray-300 bg-white hover:border-gray-400"
            }`}
          >
            {isDefault ? (
              <span className="block h-2 w-2 m-auto rounded-full bg-white" />
            ) : null}
          </button>
        );
      },
    },
  ];

  // --- Summary for the save bar ---

  const getDirtySummary = (): string => {
    const parts: string[] = [];
    if (detailsDirty) parts.push("product details");
    if (variantsDirty) {
      const count = Object.keys(variantEdits).length;
      parts.push(`${count} variant${count !== 1 ? "s" : ""}`);
    }
    if (printAreasDirty) parts.push("print areas");
    if (pricingDirty) parts.push("pricing");
    return parts.join(", ");
  };

  return (
    <PageContent className="overflow-y-auto h-screen">
      {/* Unified save bar */}
      {anyDirty && (
        <div className="sticky left-0 top-0 z-10 bg-white py-2 px-4 mb-4 rounded-xl border shadow-xs flex items-center gap-3 border-b">
          <span className="text-xs text-gray-500 mr-auto">
            Unsaved changes: {getDirtySummary()}
          </span>
          <Button variant="outline" onClick={handleDiscard}>
            Discard
          </Button>
          <Button
            loading={saveAll.isPending}
            onClick={() => setShowConfirm(true)}
          >
            <Save size={14} className="mr-1.5" />
            Save Changes
          </Button>
          {saveAll.isError && (
            <span className="text-sm text-red-500">Failed to save.</span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        <IconButton size="sm" onClick={() => navigate("/products")}>
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </IconButton>
        <div className="flex-1">
          <PageHeader
            icon={ShoppingBag}
            title={product.name}
            description={`${product.slug} · ${product.category} · Created ${formatDate(product.createdAt)}`}
            actions={
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                  product.isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {product.isActive ? "Active" : "Inactive"}
              </span>
            }
          />
        </div>
      </div>

      {/* Product info card */}
      <div className="border rounded-xl bg-white p-5">
        <div className="flex items-start justify-between gap-6">
          <div className="space-y-3 flex-1">
            <div>
              <Label className="text-xs text-gray-500 block mb-1">Name</Label>
              <TextField
                value={detailEdits.name ?? product.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDetailEdits((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500 block mb-1">
                Description
              </Label>
              <textarea
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                rows={2}
                value={detailEdits.description ?? product.description ?? ""}
                onChange={(e) =>
                  setDetailEdits((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex items-end gap-4">
              <div>
                <Label className="text-xs text-gray-500 block mb-1">
                  Base Price (EGP)
                </Label>
                <TextField
                  type="number"
                  value={detailEdits.basePrice ?? String(product.basePrice)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDetailEdits((prev) => ({
                      ...prev,
                      basePrice: e.target.value,
                    }))
                  }
                  className="w-32"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 block mb-1">
                  Currency
                </Label>
                <span className="inline-flex items-center h-9 px-3 text-sm text-gray-500 bg-gray-100 border rounded-lg">
                  EGP
                </span>
              </div>
              <div>
                <Label className="text-xs text-gray-500 block mb-1">
                  Variants
                </Label>
                <span className="inline-flex items-center h-9 px-3 text-sm text-gray-700">
                  {product.variants.length}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                updateProduct.mutate({ isActive: !product.isActive })
              }
              loading={updateProduct.isPending}
            >
              {product.isActive ? "Deactivate" : "Activate"}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6">
        <Tabs
          tabs={TABS}
          value={tab}
          onChange={setTab}
          variant="pill"
          renderTab={(t) => {
            const isDirty =
              (t.key === "variants" && variantsDirty) ||
              (t.key === "printAreas" && printAreasDirty) ||
              (t.key === "pricing" && pricingDirty);
            return isDirty ? (
              <span className="ml-1 w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
            ) : null;
          }}
        />
      </div>

      {/* Tab content */}
      <div className="mt-4 pb-8">
        {tab === "variants" && (
          <Table
            columns={variantColumns}
            data={product.variants}
            keyExtractor={(v) => v.id}
          />
        )}

        {tab === "printAreas" && printAreasLocal && (
          <div className="space-y-4">
            <PrintAreasEditor
              value={printAreasLocal}
              onChange={(updated: PrintAreas) => {
                setPrintAreasLocal(updated);
                setPrintAreasDirty(true);
              }}
            />
            {recalcResult !== null && !printAreasDirty && !pricingDirty && (
              <p className="text-sm text-green-600">
                Recalculated pricing for {recalcResult} design
                {recalcResult !== 1 ? "s" : ""}.
              </p>
            )}
          </div>
        )}

        {tab === "pricing" && pricingLocal && (
          <div className="space-y-4">
            <PricingEditor
              value={pricingLocal}
              surfaces={
                printAreasLocal?.surfaces ?? product.printAreas.surfaces
              }
              onChange={(updated: PricingConfig) => {
                setPricingLocal(updated);
                setPricingDirty(true);
              }}
            />
          </div>
        )}

        {tab === "pricing" && !pricingLocal && (
          <p className="text-sm text-gray-500">
            No pricing config. Save print areas first to set up surfaces.
          </p>
        )}
      </div>

      {/* Confirmation modal */}
      <AlertDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        title="Save Changes"
        description="The following changes will be saved:"
        body={
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {getAllChangeLines().map(({ section, lines }) => (
              <div key={section}>
                <p className="text-xs font-medium text-gray-900 mb-1">
                  {section}
                </p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {lines.map((line, i) => (
                    <li key={i} className="text-sm text-gray-600">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        }
        confirmText="Save"
        onConfirm={handleConfirmSave}
        loading={saveAll.isPending}
      />
    </PageContent>
  );
};
